import { useEffect } from 'react';
import { css } from '@emotion/react';
import { CalloutDialog } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useEligibility } from '@frontend/user-preferences';
import { useLocation, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, Text, usePopoverDialog } from '@frontend/design-system';
import {
  InsuranceEligibilityCalloutDefaultCriteria,
  InsuranceEligibilityCalloutFeatureIds,
  InsuranceEligibilityCalloutTrackingIds,
} from './constants';

const POPOVER_WIDTH = 336;
// Space in pixels between the Insurance tab and the popover.
const POPOVER_OFFSET = 12;

type InsuranceEligibilitySchedulePulseCalloutProps = {
  // Element the popover should anchor to (the Insurance tab button).
  anchorElement: HTMLElement | null;
  // Whether the Insurance tab is currently the active tab.
  isTabActive: boolean;
};

export const InsuranceEligibilitySchedulePulseCallout = ({
  anchorElement,
  isTabActive,
}: InsuranceEligibilitySchedulePulseCalloutProps) => {
  const { t } = useTranslation('schedule-pulse-insurance-tab');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Popover config: show below the tab with a small gap.
  const { getDialogProps, open, close, refs } = usePopoverDialog({
    // Render the callout below the Insurance tab.
    placement: 'bottom',
    // Add vertical spacing between tab and callout.
    middlewareOptions: { offset: POPOVER_OFFSET },
    interactionOptions: {
      dismiss: {
        // Keep it open until user clicks CTA or state hides it.
        outsidePress: false,
      },
    },
  });

  const { isEIVFeatureHiddenInAllLocation } = useHasWIVOrEIVAccess();
  // Show only when feature is available.
  const hasEligibilityListAccess = !isEIVFeatureHiddenInAllLocation;
  const { shouldShow, disableEligibility } = useEligibility({
    featureId: InsuranceEligibilityCalloutFeatureIds.pulse,
    defaultCriteria: InsuranceEligibilityCalloutDefaultCriteria,
  });
  const isInsuranceEligibilityPageOpened = pathname === '/schedule/eligibility-list';
  // Final visibility gate for this callout.
  const isVisible = hasEligibilityListAccess && shouldShow && !isInsuranceEligibilityPageOpened && isTabActive;

  useEffect(() => {
    if (!isVisible || !anchorElement) {
      close();
      return;
    }
    // Anchor popover position to the Insurance tab element passed in by the parent.
    refs.setReference(anchorElement);
    open();
  }, [isVisible, anchorElement, refs, open, close]);

  if (!isVisible) {
    return null;
  }

  return (
    <CalloutDialog
      color='seaweed'
      popoverDialogProps={getDialogProps()}
      badge='new'
      title={
        <Text weight='bold' size='medium'>
          {t('Insurance Eligibility in One Place')}
        </Text>
      }
      description={
        <Text size='medium'>
          {t('Review verification status, coverage details, and eligibility reports from a single list.')}
        </Text>
      }
      actions={
        <Button
          variant='tertiary'
          onClick={() => {
            // Dismiss callout and go to the list page.
            disableEligibility();
            close();
            navigate({ to: '/schedule/eligibility-list' });
          }}
          css={styles.openButton}
          trackingId={InsuranceEligibilityCalloutTrackingIds.schedulePulseInsuranceEligibilityCalloutOpen}
        >
          {t('Open Insurance Eligibility')}
        </Button>
      }
      // The Floating UI arrow renders just outside the dialog shell, so the shell must not clip overflow.
      containerStyles={styles.popoverShell}
      cardStyles={styles.card}
    />
  );
};

const styles = {
  popoverShell: css({
    overflow: 'visible',
  }),
  card: css({
    width: POPOVER_WIDTH,
    borderRadius: theme.borderRadius.small,
    padding: theme.spacing(1),
  }),
  openButton: css({
    ':hover': {
      backgroundColor: 'transparent',
      color: theme.colors.primary70,
    },
  }),
};
